// eslint-disable-next-line no-unused-vars
import React, { useEffect, useMemo, useState } from 'react';
import { bool } from 'prop-types';
import { useContextSelector } from 'use-context-selector';
import uniqueId from 'lodash/uniqueId';

import WishlistsMenuComponent from 'Components/Wishlists/WishlistsMenu';
import ProductWishlistItem from 'Containers/Products/ProductWishlistItem';
import { CartContext } from 'Contexts/CartContext';
import { stockAlertWishlistId, WishlistContext } from 'Contexts/WishlistContext';
import useUser from 'Hooks/useUser';

const WishlistsMenuContainer = ({ active, open }) => {
  const { cart, updateProducts } = useContextSelector(CartContext, data => ({
    cart: data?.data,
    updateProducts: data.updateProducts
  }));
  const { data, stockAlertWishlist, defaultWishlist, error, getList } = useContextSelector(
    WishlistContext,
    d => d
  );
  const { data: user } = useUser();
  const [currentItem, setCurrentItem] = useState(null);
  const [wishlist, setWishlist] = useState(null);
  const [wishlistErr, setWishlistErr] = useState(null);
  const [addAllLoading, setAllLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    if (
      !currentItem
      && defaultWishlist
    ) {
      setCurrentItem(defaultWishlist.id);
    }
  }, [defaultWishlist]);

  const handleAddListToCart = async () => {
    setAllLoading(true);
    try {
      const productsToAdd = (wishlist?.products || []).reduce((acc, product) => {
        if (
          !product.priceAvailable
          || !product.stockAvailable
        ) {
          return acc;
        }
        const existingProduct = cart?.items?.find(cartItem => (
          !cartItem.promotionId
          && (cartItem.productId === product.id)
        ));
        if (existingProduct) {
          acc.push({
            ...product,
            quantity: Math.max(
              existingProduct.minQuantity,
              Math.min(
                existingProduct.stockValue,
                existingProduct.quantity + product.quantity
              )
            )
          });
        } else {
          acc.push(product);
        }

        return acc;
      }, []);
      await updateProducts(productsToAdd);
    } finally {
      setAllLoading(false);
    }
  };

  const getWishlist = async (listId, defaultCurrency, withVat) => {
    try {
      setWishlistErr(null);
      setLoadingList(true);
      const wishlistData = await getList(listId, defaultCurrency || user?.defaultCurrency, withVat || user?.withVat);
      setLoadingList(false);
      if (wishlistData) {
        setWishlist(wishlistData);
      }
    } catch (err) {
      setWishlistErr(err?.info?.errors?.at(0).message || err.message);
    }
  };

  const handleOnGetWishlist = async id => {
    setCurrentItem(id);
    if (
      (
        id in data
        && 'products' in data[id]
      )
      || (
        id === stockAlertWishlistId
        && stockAlertWishlist?.products
      )
    ) {
      setWishlist(data[id] || stockAlertWishlist);
    } else {
      setWishlist(null);
    }
    getWishlist(id);
  };

  useEffect(() => {
    if (!defaultWishlist?.id) return;

    if (!defaultWishlist.products) {
      getWishlist(defaultWishlist.id);
      return;
    }

    if (!wishlist?.id) {
      setWishlist(defaultWishlist);
    }
  }, [defaultWishlist?.id, defaultWishlist?.products, wishlist?.id]);

  useEffect(() => {
    if (data) {
      if (
        currentItem in data
        || currentItem === stockAlertWishlistId
      ) {
        setWishlist(data[currentItem] || stockAlertWishlist);
      } else {
        const newList = Object.values(data).find(i => i.isDefault) || Object.values[0];
        if (newList) {
          if (newList.products) {
            setWishlist(newList);
          } else {
            // This part is commented so that it does not make unnecessary calls when 404 page is loaded.
            // setWishlist(null);
            // getWishlist(newList.id, user?.defaultCurrency, user?.withVat);
          }
          setCurrentItem(newList.id);
        } else {
          setWishlist(null);
          setCurrentItem(null);
        }
      }
    }
  }, [data, currentItem]);

  useEffect(() => {
    const updateListCurrency = () => {
      if (
        currentItem in data
          || currentItem === stockAlertWishlistId
      ) {
        getWishlist(data[currentItem]?.id || stockAlertWishlistId, user?.defaultCurrency, user?.withVat);
      }
    };
    updateListCurrency();
  }, [user?.defaultCurrency, user?.withVat]);

  const setNewList = async listId => {
    try {
      setWishlistErr(null);
      const r = await getList(listId);
      setWishlist(r);
    } catch (e) {
      setWishlistErr(e.message);
    }
  };

  useEffect(() => {
    // If current list has some pending products,
    // fetch the list
    if (
      active
      && open
      && wishlist?.id
      && (wishlist?.products || []).find(product => !product.code)
    ) {
      setNewList(wishlist.id);
    }
  }, [active, open, wishlist]);

  const memoizedProducts = useMemo(
    () => (wishlist?.products || [])
      .map(product => (
        <ProductWishlistItem
          key={ uniqueId(product.id) }
          parentWishlist={ wishlist }
          product={ product }
          defaultCurrency={ user?.defaultCurrency }
          withVat={ user?.withVat }
        />
      )),
    [wishlist?.products]
  );

  return (
    <>
      <WishlistsMenuComponent
        loadingList={ loadingList }
        addAllToCartLoading={ addAllLoading }
        err={ error?.message || error || null }
        wishlistErr={ wishlistErr }
        currentList={ currentItem }
        getWishlist={ handleOnGetWishlist }
        onAddListToCart={ handleAddListToCart }
        canAddAll={
          (!wishlist?.products?.filter(
            prod => (
              !prod.stockAvailable
              || !prod.priceAvailable
              || prod.warnings?.blocked_prices
              || prod.warnings?.blocked_stocks
              || prod.warnings?.blocked_orders
            )).length)
          && (wishlist?.id !== stockAlertWishlistId)
        }
      >
        { memoizedProducts }
      </WishlistsMenuComponent>
    </>
  );
};

WishlistsMenuContainer.propTypes = {
  active: bool,
  open: bool
};
WishlistsMenuContainer.displayName = 'WishlistsMenuContainer';
export default WishlistsMenuContainer;
